import type { RpcClient } from "@redotech/rpc/client";
import type { RpcClientDefinition } from "@redotech/rpc/definition";
import { useMemo, useRef, useState } from "react";
import { useHandler } from "./hook";
import { useLoad } from "./load";

export type Readonly<T> =
  T extends Array<infer E>
    ? readonly E[]
    : T extends string
      ? T
      : T extends Date
        ? Date
        : T extends Temporal.Instant
          ? Temporal.Instant
          : T extends Temporal.PlainDate
            ? Temporal.PlainDate
            : { readonly [P in keyof T]: Readonly<T[P]> };

export function useRpcData<
  D extends RpcClientDefinition,
  K extends keyof D,
  Mutations extends (keyof D)[],
  Input extends D[K]["input"],
  Output extends D[K]["output"],
>(
  client: RpcClient<D>,
  rpc: K,
  params: {
    input: Input;
    mutations?: Mutations;
    condition?: boolean;
    cache?: boolean;
    cacheKey?: (input: Input) => string;
  },
): D[K]["method"] extends "unary"
  ? {
      value?: Readonly<Output>;
      pending: boolean;
      error?: unknown;
      refresh: (clearAll?: boolean) => void;
      mutations: { [key in Mutations[number]]: RpcClient<D>[key] };
    }
  : unknown | "useRpcData only works for unary methods" {
  const cache = useRef<Map<string, Readonly<Output>>>(new Map());
  const cacheKey = useMemo(
    () =>
      params.cacheKey
        ? params.cacheKey(params.input)
        : JSON.stringify(params.input),
    [params],
  );
  const [reloadSymbol, setReloadSymbol] = useState(Symbol());

  const dataLoad = useLoad(
    async (signal) => {
      if (params.condition === false) {
        return;
      }
      if (params.cache && cache.current.has(cacheKey)) {
        return cache.current.get(cacheKey)!;
      }
      const response: Output = await client[rpc](params.input, { signal });
      if (params.cache && typeof response !== "function") {
        cache.current.set(cacheKey, response);
      }
      return response;
    },
    [cacheKey, reloadSymbol, params.condition],
  );

  const cachedValue = useMemo(() => {
    return params.cache ? cache.current.get(cacheKey) : undefined;
  }, [cacheKey, params.cache]);

  const refresh = useHandler((clearAll = false) => {
    if (clearAll) {
      cache.current.clear();
    } else {
      cache.current.delete(cacheKey);
    }
    setReloadSymbol(Symbol());
  });

  const mutations = useMemo(() => {
    const mutations = {} as { [key in Mutations[number]]: RpcClient<D>[key] };
    if (!params.mutations?.length) {
      return mutations;
    }
    for (const mutation of params.mutations) {
      if (client[mutation].method === "unary") {
        mutations[mutation] = (async (input, options) => {
          try {
            return await client[mutation](input, options);
          } finally {
            refresh(true);
          }
        }) as RpcClient<D>[typeof mutation];
      } else {
        mutations[mutation] = ((input, options) => {
          try {
            return client[mutation](input, options);
          } finally {
            refresh(true);
          }
        }) as RpcClient<D>[typeof mutation];
      }
    }

    return mutations;
  }, [params.mutations, client, refresh]);

  return {
    ...(params.condition === false
      ? { pending: false, value: undefined }
      : cachedValue
        ? { pending: false, value: cachedValue }
        : dataLoad),
    refresh,
    mutations,
  };
}
